import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Typography, Button, Alert, Divider, Stack, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { useColas } from "../hooks";
import { colaSinNombre, rutaCola } from "../helpers/colas";
import AuthService from "../../../services/auth.service";
import Roles from "../../../config/roles";

const sxBotonCola = {
    justifyContent: "space-between",
    minHeight: 64,
    px: 3,
    fontSize: 20,
    fontWeight: 600,
    textTransform: "none",
    color: "#212529",
    borderColor: "#dee2e6",
    "&:hover": { borderColor: "#cc071e", backgroundColor: "#fdece9" },
};

// Qué pantalla abre cada cuenta: el kiosko saca turnos, el display muestra llamadas y la
// pantalla del almacén los turnos con sus pedidos
const DESTINOS = {
    [Roles.PANTALLA_TICKET.role]: { accion: "registro", subtitulo: "Este dispositivo sacará turnos en la cola que elijas" },
    [Roles.PANTALLA_LLAMADAS.role]: { accion: "pantalla", subtitulo: "Este dispositivo mostrará los llamamientos de la cola que elijas" },
    [Roles.PANTALLA_ALMACEN.role]: { accion: "pantalla", subtitulo: "Este dispositivo mostrará los turnos de la cola que elijas con sus pedidos pendientes" },
};

/**
 * Inicio de las cuentas de pantalla de colas (/colas/seleccionar): lista las colas
 * y abre el registro de turnos (kiosko) o la pantalla de llamadas (display) de la
 * elegida. Como estas cuentas no tienen cabecera, aquí está el cierre de sesión.
 */
const SeleccionCola = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const navigate = useNavigate();
    const { colas, loading, error, recargar } = useColas();

    const destino = useMemo(() => {
        const roles = AuthService.getCurrentUser()?.roles ?? [];
        return DESTINOS[roles.find((rol) => DESTINOS[rol])] ?? DESTINOS[Roles.PANTALLA_LLAMADAS.role];
    }, []);

    const handleCerrarSesion = useCallback(() => {
        AuthService.logout();
        navigate("/login", { replace: true });
    }, [navigate]);

    const renderContenido = () => {
        if (loading) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: "#cc071e" }} />
                </Box>
            );
        }
        if (error) {
            return (
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={recargar}>Reintentar</Button>}
                >
                    {error}
                </Alert>
            );
        }
        if (!colas.length) {
            return (
                <Typography variant="body2" sx={{ color: "#6c757d", textAlign: "center", py: 4 }}>
                    No hay colas creadas
                </Typography>
            );
        }
        return (
            <Stack spacing={2}>
                {colas.map((cola) => (
                    <Button
                        key={cola.id}
                        variant="outlined"
                        fullWidth
                        disabled={colaSinNombre(cola)}
                        endIcon={<ChevronRightIcon />}
                        onClick={() => navigate(rutaCola(cola, destino.accion))}
                        sx={sxBotonCola}
                    >
                        {cola.nombre || "-"}
                    </Button>
                ))}
            </Stack>
        );
    };

    return (
        <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: { xs: 2, md: 5 } }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <ListAltIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Elige la cola
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                {destino.subtitulo}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LogoutIcon />}
                            onClick={handleCerrarSesion}
                            sx={{ color: "#495057", borderColor: "#6c757d", textTransform: "none", flexShrink: 0 }}
                        >
                            {isMobile ? "Salir" : "Cerrar sesión"}
                        </Button>
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                </Box>
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>{renderContenido()}</Box>
            </Card>
        </Box>
    );
};

export default SeleccionCola;
